/** Fehlerantwort der APIs beider Apps. */
export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: Partial<ApiError>) {
    super(body.message ?? `Fehler ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.error ?? 'unknown';
    this.details = body.details;
  }
}

export class NetworkError extends Error {
  constructor() {
    super('Keine Verbindung zum Server');
    this.name = 'NetworkError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

async function send(path: string, { method = 'GET', body, token, signal }: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new NetworkError();
  }
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Partial<ApiError>;
    throw new ApiRequestError(response.status, errorBody);
  }
  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  return (await response.json()) as T;
}

/** Lädt eine Datei (z. B. CSV-Export mit Login) und bietet sie zum Download an. */
export async function downloadFile(path: string, fileName: string, token: string | null): Promise<void> {
  const response = await send(path, { token });
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/** Öffnet eine geschützte Datei (z. B. Druck-PDF mit Login) in einem neuen Tab. */
export async function openFile(path: string, token: string | null): Promise<void> {
  // Tab sofort öffnen – sonst blockiert der Browser das Pop-up nach dem asynchronen Laden.
  const tab = window.open('', '_blank');
  const response = await send(path, { token });
  const url = URL.createObjectURL(await response.blob());
  if (tab) {
    tab.location.href = url;
  } else {
    window.location.href = url;
  }
}

/** Für Antworten, die kein JSON sind (z. B. die HTML-Vorschau im CMS). */
export async function apiText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await send(path, options);
  return response.text();
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}
